import React from 'react';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import classNames from 'classnames';
import { isNull } from 'lodash';
import { Loader } from '@/components/common/Loader';
import { MarkdownText } from '@/components/common/MarkdownText';
import ReportLink from '@/components/pages/Reports/shared/ReportLink';
import { useCurrentUser } from '@/core/hooks/useCurrentUser';
import { IncidentItem } from '@/server/services/GoogleBigQuery/BigQueryIncidentsClient.types';
import { PocProceedButton } from '@/components/pages/SituationalAwareness/components/MapDemoControls/components/PocProceedButton';
import {
  isPocAlarmEvent,
  isPocAlarmIncidentTypeName,
  isPocDemoOrgId,
} from '@/components/pages/SituationalAwareness/components/MapDemoControls/isNavigationDemoOrgId';
import styles from './TargetCard.module.scss';
import { TargetDetails, type TargetDetailsProps } from './TargetDetails';
import { TargetVideo, type TargetVideoProps } from './TargetVideo';

export interface TargetCardProps {
  className?: string;
  details: TargetDetailsProps;
  imageSrc?: string | null;
  videoSrc?: TargetVideoProps['videoSrc'];
  videoSrcType?: TargetVideoProps['videoSrcType'];
  description: string;
  isMediaLoading?: boolean;
  link?: string;
  reportLink?: string;
  isDetailsShow?: boolean;
  /**
   * Only for POC Demo navigation
   */
  incident: Pick<IncidentItem, 'DataSource' | 'IncidentTypeName'> | null;
}

const isGoogleDriveImage = (imageSrc = '') => !!imageSrc?.match('drive.google');
const getGoogleFileId = (imageSrc: string) => imageSrc.split('/file/d/')[1].split('/')[0];
const getGoogleDriveThumbnail = (imageSrc: string) =>
  `https://drive.google.com/thumbnail?id=${getGoogleFileId(imageSrc)}&sz=w420`;

const MediaLoader: React.FC<Pick<TargetCardProps, 'isMediaLoading'>> = ({ isMediaLoading }) => {
  return isMediaLoading ? (
    <div className={styles.image}>
      <Loader />
    </div>
  ) : null;
};

const TargetImage: React.FC<Pick<TargetCardProps, 'imageSrc'>> = ({ imageSrc }) => {
  const googleImageLink = imageSrc && isGoogleDriveImage(imageSrc) ? getGoogleDriveThumbnail(imageSrc) : null;

  return (
    <div key={imageSrc} className={styles.image}>
      {imageSrc && !googleImageLink && <img src={imageSrc} />}
      {googleImageLink && <img src={googleImageLink} />}
    </div>
  );
};

export const TargetCard: React.FC<TargetCardProps> = ({
  className,
  details,
  isMediaLoading,
  imageSrc,
  videoSrc,
  videoSrcType,
  description,
  link,
  incident,
  reportLink,
  isDetailsShow = true,
}) => {
  const t = useTranslations();
  const { orgId } = useCurrentUser();
  const showProceedBtn =
    incident && isPocDemoOrgId(orgId) && isPocAlarmEvent(incident) && isPocAlarmIncidentTypeName(incident);
  const isAssetCard = isNull(incident);

  return (
    <div className={classNames(styles.container, className)}>
      {isDetailsShow && <TargetDetails {...details} sourceLink={link} />}
      {showProceedBtn && <PocProceedButton incident={incident} />}
      <MediaLoader isMediaLoading={isMediaLoading} />
      {imageSrc && !videoSrc && <TargetImage key={link} imageSrc={imageSrc} />}
      {videoSrc && <TargetVideo key={link} videoSrc={videoSrc} videoSrcType={videoSrcType} />}
      <div className={styles.description}>
        <MarkdownText text={description} fallbackOnError={description} forceBlock={false} />
      </div>

      {link &&
        !reportLink &&
        isAssetCard && ( // link is shown for assets only
          <div className={styles.linksWrapper}>
            <Link className={styles.link} href={link} target="_blank" rel="noreferrer">
              {t('see-details')}
            </Link>
          </div>
        )}
      {reportLink && (
        <div className={styles.linksWrapper}>
          <ReportLink reportName={reportLink} />
        </div>
      )}
    </div>
  );
};
